/* eslint-disable complexity */
/* eslint-disable max-statements */
import { useMemo } from 'react';
import { CONTROLLED_DATE_TIME_PICKER_TYPES } from '../constants/ControlledDateTimePickerTypes.js';
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';
import { getIsDate, getIsDateTime } from '../utils/typeGuards.js';

const getDateTimeFlags = (props: DSControlledDateTimePickerT.InternalDateTimePickerProps) => {
  const { type, isClearable, dateTime, autoFocus, innerRef } = props;
  const constants = {
    hideDatePicker: true,
    hideTimePicker: true,
    isDateSelector: false,
    isTimeSelector: false,
    isDateTimeSelector: true,
    isWithDateInputsOnly: false,
    isWithTimeInputsOnly: false,
    autoFocusHourInput: false,
    autoFocusHourTimeWheel: false,
  };
  const withAnyInputs =
    type === CONTROLLED_DATE_TIME_PICKER_TYPES.DATE_TIME.FULL ||
    type === CONTROLLED_DATE_TIME_PICKER_TYPES.DATE_TIME.INPUTS;
  const hideDateTimePicker =
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.DATE_TIME.FULL &&
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.DATE_TIME.PICKER &&
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.DATE_TIME.CONTROLLER_ONLY;
  const isControllerOnly = type === CONTROLLED_DATE_TIME_PICKER_TYPES.DATE_TIME.CONTROLLER_ONLY;
  const withAnyPicker = !hideDateTimePicker;
  const withClearBtn = withAnyInputs && isClearable && dateTime !== '' && dateTime !== '__/__/____ __:__ __';
  const shouldPreserveClearableSpace = isClearable && withAnyInputs;
  const finalFlags = {
    hideDate: !withAnyInputs,
    hideTime: !withAnyInputs,
    hideDateTimePicker,
    isControllerOnly,
    isWithTimeWheelToo: !hideDateTimePicker,
    isWithCalendarToo: !hideDateTimePicker,
    withAnyInputs,
    withAnyPicker,
    withClearBtn,
    isWithDateInputs: withAnyInputs,
    isWithTimeInputs: withAnyInputs,
    autoFocusMonthInput: withAnyInputs && autoFocus,
    autoFocusPrevMonthArrow: isControllerOnly && autoFocus,
    innerRefMonthInput: withAnyInputs ? innerRef : undefined,
    innerRefPrevMonthArrow: isControllerOnly ? innerRef : undefined,
    shouldPreserveClearableSpace,
    withAnyRightController: withAnyPicker || withClearBtn || shouldPreserveClearableSpace,
    ...constants,
  };
  return finalFlags;
};
const getDateFlags = (props: DSControlledDateTimePickerT.InternalDatePickerProps) => {
  const { type, isClearable, date, autoFocus, innerRef } = props;

  const constants = {
    isDateSelector: true,
    hideTime: true,
    hideDateTimePicker: true,
    hideTimePicker: true,
    isTimeSelector: false,
    isDateTimeSelector: false,
    isWithCalendarToo: false,
    isWithTimeWheelToo: false,
    isWithTimeInputs: false,
    isWithTimeInputsOnly: false,
    autoFocusHourTimeWheel: false,
    autoFocusHourInput: false,
  };

  const withAnyInputs =
    type === CONTROLLED_DATE_TIME_PICKER_TYPES.DATE.FULL || type === CONTROLLED_DATE_TIME_PICKER_TYPES.DATE.INPUTS;
  const hideDatePicker =
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.DATE.FULL &&
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.DATE.PICKER &&
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.DATE.CONTROLLER_ONLY;
  const isControllerOnly = type === CONTROLLED_DATE_TIME_PICKER_TYPES.DATE.CONTROLLER_ONLY;

  const withAnyPicker = !hideDatePicker;
  const withClearBtn = withAnyInputs && isClearable && date !== '' && date !== '__/__/____';
  const shouldPreserveClearableSpace = isClearable && withAnyInputs;

  const finalFlags = {
    hideDate: !withAnyInputs,
    hideDatePicker,
    isControllerOnly,
    withAnyInputs,
    withAnyPicker,
    withClearBtn,
    isWithDateInputs: withAnyInputs,
    isWithDateInputsOnly: withAnyInputs,
    autoFocusMonthInput: withAnyInputs && autoFocus,
    autoFocusPrevMonthArrow: isControllerOnly && autoFocus,
    innerRefMonthInput: withAnyInputs ? innerRef : undefined,
    innerRefPrevMonthArrow: isControllerOnly ? innerRef : undefined,
    shouldPreserveClearableSpace,
    withAnyRightController: withAnyPicker || withClearBtn || shouldPreserveClearableSpace,
    ...constants,
  };
  return finalFlags;
};
const getTimeFlags = (props: DSControlledDateTimePickerT.InternalTimePickerProps) => {
  const { type, isClearable, time, autoFocus, innerRef } = props;
  const constants = {
    hideDate: true,
    hideDateTimePicker: true,
    hideDatePicker: true,
    isDateSelector: false,
    isDateTimeSelector: false,
    isWithDateInputs: false,
    isWithDateInputsOnly: false,
    isTimeSelector: true,
    isWithTimeWheelToo: false,
    isWithCalendarToo: false,
    autoFocusMonthInput: false,
    autoFocusPrevMonthArrow: false,
    innerRefMonthInput: undefined,
    innerRefPrevMonthArrow: undefined,
  };
  const withAnyInputs =
    type === CONTROLLED_DATE_TIME_PICKER_TYPES.TIME.FULL || type === CONTROLLED_DATE_TIME_PICKER_TYPES.TIME.INPUTS;
  const hideTimePicker =
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.TIME.FULL &&
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.TIME.PICKER &&
    type !== CONTROLLED_DATE_TIME_PICKER_TYPES.TIME.CONTROLLER_ONLY;
  const isControllerOnly = type === CONTROLLED_DATE_TIME_PICKER_TYPES.TIME.CONTROLLER_ONLY;
  const withClearBtn = withAnyInputs && isClearable && time !== '' && time !== '__:__ __';
  const shouldPreserveClearableSpace = isClearable && withAnyInputs;
  const withAnyPicker = !hideTimePicker;
  const finalFlags = {
    hideTime: !withAnyInputs,
    hideTimePicker,
    isControllerOnly,
    withAnyInputs,
    withAnyPicker,
    withClearBtn,
    isWithTimeInputs: withAnyInputs,
    isWithTimeInputsOnly: withAnyInputs,
    autoFocusHourInput: withAnyInputs && autoFocus,
    autoFocusHourTimeWheel: isControllerOnly && autoFocus,
    innerRefHourInput: withAnyInputs ? innerRef : undefined,
    innerRefHourTimeWheel: isControllerOnly ? innerRef : undefined,
    shouldPreserveClearableSpace,
    withAnyRightController: withAnyPicker || withClearBtn || shouldPreserveClearableSpace,
    ...constants,
  };
  return finalFlags;
};

export const useGetFlags = (props: DSControlledDateTimePickerT.InternalProps) =>
  useMemo(() => {
    if (getIsDateTime(props)) {
      return getDateTimeFlags(props);
    }
    if (getIsDate(props)) {
      return getDateFlags(props);
    }
    return getTimeFlags(props);
  }, [props]);
